import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

import { IEnvironment } from '../interfaces/environment.interface';
import { ENVIRONMENT_CONFIG } from '../providers/environment.provider';

export interface IGoogleMapsMarker {
  id: number;
  position: { lat: number; lng: number };
  name: string;
  address: string;
  phone: string;
  finalized: boolean;
}

@Injectable({ providedIn: 'root' })
export class GoogleMapsService {
  private environment = inject<IEnvironment>(ENVIRONMENT_CONFIG);
  private scriptLoadingPromise!: Promise<void>;
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  stops = signal<IGoogleMapsMarker[]>([]);
  activeStop = signal<IGoogleMapsMarker | null>(null);

  openMarker(stop: IGoogleMapsMarker) {
    this.activeStop.set(stop);
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  load(): Promise<void> {
    if (!this.isBrowser) {
      return Promise.resolve();
    }

    if (this.scriptLoadingPromise) {
      return this.scriptLoadingPromise;
    }

    const apiKey = this.environment.GOOGLE_MAPS.API_KEY;
    const script = this.document.createElement('script');

    this.scriptLoadingPromise = new Promise((resolve, reject) => {
      const win = window as any;
      const existing = this.document.querySelector('script[data-google-maps-loader]');

      if (win.google?.maps && win.google?.maps?.marker?.AdvancedMarkerElement) {
        resolve();
        return;
      }

      if (existing) {
        this.waitForMaps(resolve);
        return;
      }

      script.setAttribute('data-google-maps-loader', 'true');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=marker,geometry,places&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => this.waitForMaps(resolve);
      script.onerror = (err) => {
        this.scriptLoadingPromise = undefined!;
        reject(err);
      };

      this.document.body.appendChild(script);
    });

    return this.scriptLoadingPromise;
  }

  private waitForMaps(resolve: () => void) {
    const check = () => {
      if ((window as any).google?.maps?.Map && (window as any).google?.maps?.marker?.AdvancedMarkerElement) {
        resolve();
      } else {
        requestAnimationFrame(check);
      }
    };
    check();
  }
}
